import React, { useEffect } from 'react';
import { DataGrid, GridColumnVisibilityModel, GridFilterModel, GridSortModel, GridColDef } from '@mui/x-data-grid';
import { ChartProps } from '../Chart';
import {
  evaluateRulesOnDict,
  evaluateSingleRuleOnDict,
  generateClassDefinitionsBasedOnRules,
  useStyleRules,
} from '../../extensions/styling/StyleRuleEvaluator';
import { Tooltip, Snackbar } from '@mui/material';
import { downloadCSV } from '../ChartUtils';
import { getRendererForValue, rendererForType, RenderSubValue } from '../../report/ReportRecordProcessing';

import {
  getRule,
  executeActionRule,
  getPageNumbersAndNamesList,
  performActionOnElement,
} from '../../extensions/advancedcharts/Utils';

import { IconButton } from '@neo4j-ndl/react';
import { CloudArrowDownIconOutline, XMarkIconOutline } from '@neo4j-ndl/react/icons';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { extensionEnabled } from '../../utils/ReportUtils';
import { getCheckboxes, hasCheckboxes, updateCheckBoxes } from './TableActionsHelper';
import { connect } from 'react-redux';
import { setSessionStorageValue } from '../../sessionStorage/SessionStorageActions';
import { getSessionStorageValue } from '../../sessionStorage/SessionStorageSelectors';

const TABLE_ROW_HEIGHT = 52;
const HIDDEN_COLUMN_PREFIX = '__';
const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 5;
const TABLE_PAGINATION_PREFIX = 'table_pagination';
const TABLE_COLUMN_VISIBILITY_PREFIX = 'table_column_visibility';

const getTablePaginationKey = (pagenumber: string | number, cardId: string) =>
  `${TABLE_PAGINATION_PREFIX}__${pagenumber}__${cardId}`;

const getColumnVisibilityKey = (pagenumber: string | number, cardId: string) =>
  `${TABLE_COLUMN_VISIBILITY_PREFIX}__${pagenumber}__${cardId}`;

const TABLE_COLUMN_WIDTHS_PREFIX = 'table_column_widths';
const TABLE_FILTER_MODEL_PREFIX = 'table_filter_model';
const TABLE_SORT_MODEL_PREFIX = 'table_sort_model';

const getColumnWidthsKey = (pagenumber: string | number, cardId: string) =>
  `${TABLE_COLUMN_WIDTHS_PREFIX}__${pagenumber}__${cardId}`;

const getFilterModelKey = (pagenumber: string | number, cardId: string) =>
  `${TABLE_FILTER_MODEL_PREFIX}__${pagenumber}__${cardId}`;

const getSortModelKey = (pagenumber: string | number, cardId: string) =>
  `${TABLE_SORT_MODEL_PREFIX}__${pagenumber}__${cardId}`;

const parseHiddenColumns = (value: any): string[] => {
  if (!value || value === '[]') {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const calculatePageSizeOptions = (defaultRowsPerPage: number): number[] => {
  const options = [...DEFAULT_PAGE_SIZE_OPTIONS];
  if (!options.includes(defaultRowsPerPage) && defaultRowsPerPage > 0) {
    options.push(defaultRowsPerPage);
    options.sort((a, b) => a - b);
  }
  return options;
};

const validatePageSize = (value: any, fallback: number): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) || parsed <= 0 ? fallback : parsed;
};

const theme = createTheme({
  typography: {
    fontFamily: "'Nunito Sans', sans-serif !important",
    allVariants: { color: 'rgb(var(--palette-neutral-text-default))' },
  },
});
const fallbackRenderer = (value) => {
  return JSON.stringify(value);
};

function renderAsButtonWrapper(renderer) {
  return function renderAsButton(value) {
    const outputValue = renderer(value, true);
    // If there's nothing to be rendered, there's no button needed.
    if (outputValue == '') {
      return <></>;
    }
    return (
      <Button style={{ width: '100%', marginLeft: '5px', marginRight: '5px' }} variant='contained' color='primary'>
        {outputValue}
      </Button>
    );
  };
}

function ApplyColumnType(column, value, asAction) {
  const renderer = getRendererForValue(value);
  const renderCell = asAction ? renderAsButtonWrapper(renderer.renderValue) : renderer.renderValue;
  const columnProperties = renderer
    ? { type: renderer.type, renderCell: renderCell ? renderCell : fallbackRenderer }
    : rendererForType.string;
  if (columnProperties) {
    column = { ...column, ...columnProperties };
  }
  return column;
}

export const generateSafeColumnKey = (key) => {
  return key != 'id' ? key : `${key} `;
};

const NeoTableChartComponent = (
  props: ChartProps & {
    sessionPageSize?: number;
    setSessionPageSize?: (key: string, value: number) => void;
    sessionColumnVisibility?: GridColumnVisibilityModel;
    setSessionColumnVisibility?: (key: string, value: GridColumnVisibilityModel) => void;
    sessionColumnWidths?: Record<string, number>;
    setSessionColumnWidths?: (key: string, value: Record<string, number>) => void;
    sessionFilterModel?: GridFilterModel;
    setSessionFilterModel?: (key: string, value: GridFilterModel) => void;
    sessionSortModel?: GridSortModel;
    setSessionSortModel?: (key: string, value: GridSortModel) => void;
  }
) => {
  const transposed = props.settings && props.settings.transposed ? props.settings.transposed : false;
  const wrapContent = props.settings && props.settings.wrapContent ? props.settings.wrapContent : false;
  const allowDownload =
    props.settings && props.settings.allowDownload !== undefined ? props.settings.allowDownload : false;

  const actionsRules =
    extensionEnabled(props.extensions, 'actions') && props.settings && props.settings.actionsRules
      ? props.settings.actionsRules
      : [];
  const compact = props.settings && props.settings.compact !== undefined ? props.settings.compact : false;
  const styleRules = useStyleRules(
    extensionEnabled(props.extensions, 'styling'),
    props.settings.styleRules,
    props.getGlobalParameter
  );

  const [notificationOpen, setNotificationOpen] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>({});

  // Pagination state management
  const { id, pagenumber } = props;
  const defaultRowsPerPage = validatePageSize(props.settings?.defaultRowsPerPage, DEFAULT_PAGE_SIZE);
  const pageSizeOptions = React.useMemo(() => calculatePageSizeOptions(defaultRowsPerPage), [defaultRowsPerPage]);

  const initialPageSize = React.useMemo(() => {
    if (props.sessionPageSize !== undefined && props.sessionPageSize > 0) {
      return props.sessionPageSize;
    }
    return defaultRowsPerPage;
  }, [props.sessionPageSize, defaultRowsPerPage]);

  const [currentPageSize, setCurrentPageSize] = React.useState(initialPageSize);

  const sessionKey = React.useMemo(() => {
    return id && pagenumber !== undefined ? getTablePaginationKey(pagenumber, id) : null;
  }, [id, pagenumber]);

  const columnVisibilitySessionKey = React.useMemo(() => {
    return id && pagenumber !== undefined ? getColumnVisibilityKey(pagenumber, id) : null;
  }, [id, pagenumber]);

  const columnWidthsSessionKey = React.useMemo(() => {
    return id && pagenumber !== undefined ? getColumnWidthsKey(pagenumber, id) : null;
  }, [id, pagenumber]);

  const filterModelSessionKey = React.useMemo(() => {
    return id && pagenumber !== undefined ? getFilterModelKey(pagenumber, id) : null;
  }, [id, pagenumber]);

  const sortModelSessionKey = React.useMemo(() => {
    return id && pagenumber !== undefined ? getSortModelKey(pagenumber, id) : null;
  }, [id, pagenumber]);

  // State for filter and sort models
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    props.sessionFilterModel || { items: [] }
  );

  const [sortModel, setSortModel] = React.useState<GridSortModel>(props.sessionSortModel || []);

  // Track user-resized column widths (field -> width mapping)
  const [userColumnWidths, setUserColumnWidths] = React.useState<Record<string, number>>(
    props.sessionColumnWidths || {}
  );

  const handlePaginationModelChange = React.useCallback(
    (model: { pageSize: number; page: number }) => {
      setCurrentPageSize(model.pageSize);
      if (sessionKey && props.setSessionPageSize) {
        props.setSessionPageSize(sessionKey, model.pageSize);
      }
    },
    [sessionKey, props.setSessionPageSize]
  );

  const handleColumnWidthChange = React.useCallback(
    (params: { colDef: GridColDef; width: number }) => {
      const newWidths = { ...userColumnWidths, [params.colDef.field]: params.width };
      setUserColumnWidths(newWidths);
      if (columnWidthsSessionKey && props.setSessionColumnWidths) {
        props.setSessionColumnWidths(columnWidthsSessionKey, newWidths);
      }
    },
    [userColumnWidths, columnWidthsSessionKey, props.setSessionColumnWidths]
  );

  const handleFilterModelChange = React.useCallback(
    (newFilterModel: GridFilterModel) => {
      setFilterModel(newFilterModel);
      if (filterModelSessionKey && props.setSessionFilterModel) {
        props.setSessionFilterModel(filterModelSessionKey, newFilterModel);
      }
    },
    [filterModelSessionKey, props.setSessionFilterModel]
  );

  const handleSortModelChange = React.useCallback(
    (newSortModel: GridSortModel) => {
      setSortModel(newSortModel);
      if (sortModelSessionKey && props.setSessionSortModel) {
        props.setSessionSortModel(sortModelSessionKey, newSortModel);
      }
    },
    [sortModelSessionKey, props.setSessionSortModel]
  );

  const useStyles = generateClassDefinitionsBasedOnRules(styleRules);
  const classes = useStyles();
  const tableRowHeight = compact ? TABLE_ROW_HEIGHT / 2 : TABLE_ROW_HEIGHT;

  const columnWidthsType =
    props.settings && props.settings.columnWidthsType ? props.settings.columnWidthsType : 'Relative (%)';
  let columnWidths = null;
  try {
    columnWidths = props.settings && props.settings.columnWidths && JSON.parse(props.settings.columnWidths);
  } catch (e) {
    // do nothing
  } finally {
    // do nothing
  }

  const { records } = props;

  const generateSafeColumnKey = (key) => {
    return key != 'id' ? key : `${key} `;
  };

  const actionableFields = actionsRules.filter((r) => r.condition !== 'rowCheck').map((r) => r.field);
  const columns = transposed
    ? [records[0].keys[0]].concat(records.map((record) => record._fields[0]?.toString() || '')).map((key, i) => {
        const uniqueKey = `${String(key)}_${i}`;
        const fieldKey = generateSafeColumnKey(uniqueKey);
        const userWidth = userColumnWidths[fieldKey];
        return ApplyColumnType(
          {
            key: `col-key-${i}`,
            field: fieldKey,
            headerName: generateSafeColumnKey(key),
            headerClassName: 'table-small-header',
            disableColumnSelector: true,
            // If user has resized, use their width; otherwise use flex
            ...(userWidth !== undefined
              ? { width: userWidth }
              : { flex: columnWidths && i < columnWidths.length ? columnWidths[i] : 1 }),
            disableClickEventBubbling: true,
          },
          key,
          actionableFields.includes(key)
        );
      })
    : records[0] &&
      records[0].keys &&
      records[0].keys.map((key, i) => {
        const value = records[0].get(key);
        const fieldKey = generateSafeColumnKey(key);
        const userWidth = userColumnWidths[fieldKey];
        if (columnWidthsType == 'Relative (%)') {
          return ApplyColumnType(
            {
              key: `col-key-${i}`,
              field: fieldKey,
              headerName: fieldKey,
              headerClassName: 'table-small-header',
              disableColumnSelector: true,
              // If user has resized, use their width; otherwise use flex
              ...(userWidth !== undefined
                ? { width: userWidth }
                : { flex: columnWidths && i < columnWidths.length ? columnWidths[i] : 1 }),
              disableClickEventBubbling: true,
            },
            value,
            actionableFields.includes(key)
          );
        }
        return ApplyColumnType(
          {
            key: `col-key-${i}`,
            field: fieldKey,
            headerName: fieldKey,
            headerClassName: 'table-small-header',
            disableColumnSelector: true,
            // If user has resized, use their width; otherwise use configured/default width
            width: userWidth !== undefined ? userWidth : (columnWidths && i < columnWidths.length ? columnWidths[i] : 100),
            disableClickEventBubbling: true,
          },
          value,
          actionableFields.includes(key)
        );
      });

  useEffect(() => {
    // If session has saved visibility, use it (user override takes precedence)
    if (props.sessionColumnVisibility && Object.keys(props.sessionColumnVisibility).length > 0) {
      setColumnVisibilityModel(props.sessionColumnVisibility);
      return;
    }

    // Build initial visibility from settings and prefix convention
    const hiddenByPrefix = Object.assign(
      {},
      ...columns.filter((x) => x.field.startsWith(HIDDEN_COLUMN_PREFIX)).map((x) => ({ [x.field]: false }))
    );

    const configuredHiddenColumns = parseHiddenColumns(props.settings?.hiddenColumns);
    const hiddenBySettings = Object.assign(
      {},
      ...columns
        .filter((x) => configuredHiddenColumns.includes(x.headerName) || configuredHiddenColumns.includes(x.field))
        .map((x) => ({ [x.field]: false }))
    );

    setColumnVisibilityModel({ ...hiddenByPrefix, ...hiddenBySettings });
  }, [records, props.sessionColumnVisibility]);

  if (props.records == null || props.records.length == 0 || props.records[0].keys == null) {
    return <>No data, re-run the report.</>;
  }
  const getTransposedRows = (records) => {
    // Skip first key
    const rowKeys = [...records[0].keys];
    rowKeys.shift();

    // Add values in rows
    const rowsWithValues = rowKeys.map((key, i) =>
      Object.assign(
        { id: i, Field: key },
        ...records.map((record, j) => ({
          // Note the true here is for the rendered to know we are inside a transposed table
          // It will be needed for rendering the records properly, if they are arrays
          [`${record._fields[0]}_${j + 1}`]: RenderSubValue(record._fields[i + 1], true),
        }))
      )
    );

    // Add field in rows
    const rowsWithFieldAndValues = rowsWithValues.map((row, i) => ({
      ...row,
      [`${records[0].keys[0]}_${0}`]: rowKeys[i],
    }));

    return rowsWithFieldAndValues;
  };

  const rows = transposed
    ? getTransposedRows(records)
    : records.map((record, rownumber) => {
        return Object.assign(
          { id: rownumber },
          ...record._fields.map((field, i) => ({ [generateSafeColumnKey(record.keys[i])]: field }))
        );
      });

  const pageNames = getPageNumbersAndNamesList();
  const customStyles = { '&.MuiDataGrid-root .MuiDataGrid-footerContainer > div': { marginTop: '0px' } };

  const commonGridProps = {
    key: 'tableKey',
    columnHeaderHeight: 32,
    rowHeight: tableRowHeight,
    rows: rows,
    columns: columns,
    pageSizeOptions: pageSizeOptions,
    initialState: {
      pagination: {
        paginationModel: {
          pageSize: currentPageSize,
          page: 0,
        },
      },
    },
    onPaginationModelChange: handlePaginationModelChange,
    columnVisibilityModel: columnVisibilityModel,
    onColumnVisibilityModelChange: (newModel) => {
      setColumnVisibilityModel(newModel);
      if (columnVisibilitySessionKey && props.setSessionColumnVisibility) {
        props.setSessionColumnVisibility(columnVisibilitySessionKey, newModel);
      }
    },
    onCellClick: (e) => performActionOnElement(e, actionsRules, { ...props, pageNames: pageNames }, 'Click', 'Table'),
    onCellDoubleClick: (e) => {
      let rules = getRule(e, actionsRules, 'doubleClick');
      if (rules !== null) {
        rules.forEach((rule) => executeActionRule(rule, e, { ...props, pageNames: pageNames }, 'table'));
      } else {
        setNotificationOpen(true);
        navigator.clipboard.writeText(e.value);
      }
    },
    checkboxSelection: hasCheckboxes(actionsRules),
    rowSelectionModel: getCheckboxes(actionsRules, rows, props.getGlobalParameter),
    onRowSelectionModelChange: (selection) => updateCheckBoxes(actionsRules, rows, selection, props.setGlobalParameter),
    disableRowSelectionOnClick: true,
    filterModel: filterModel,
    onFilterModelChange: handleFilterModelChange,
    sortModel: sortModel,
    onSortModelChange: handleSortModelChange,
    onColumnWidthChange: handleColumnWidthChange,
    components: {
      ColumnSortedDescendingIcon: () => <></>,
      ColumnSortedAscendingIcon: () => <></>,
    },
    getRowClassName: (params) => {
      return ['row color', 'row text color']
        .map((e) => {
          return `rule${evaluateRulesOnDict(params.row, styleRules, [e])}`;
        })
        .join(' ');
    },
    getCellClassName: (params) => {
      return ['cell color', 'cell text color']
        .map((e) => {
          let validRuleClass = '';
          for (const [index, rule] of styleRules.entries()) {
            let ruleClass = '';
            // If the rule target is not the current cell
            if (rule.targetField) {
              if (rule.targetField === params.field) {
                ruleClass = `rule${evaluateSingleRuleOnDict({ [rule.field]: params.row[rule.field] }, rule, index, [
                  e,
                ])}`;
              }
            }
            // If the rule target is the current cell
            else {
              ruleClass = `rule${evaluateSingleRuleOnDict({ [params.field]: params.value }, rule, index, [e])}`;
            }
            // If rule class is valid (rule-1 means rule check has failed)
            if (ruleClass && ruleClass !== 'rule-1') {
              validRuleClass = ruleClass;
              break;
            }
          }
          return validRuleClass;
        })
        .join(' ');
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root} style={{ height: '100%', width: '100%', position: 'relative' }}>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={notificationOpen}
          autoHideDuration={2000}
          onClose={() => setNotificationOpen(false)}
          message='Value copied to clipboard.'
          action={
            <React.Fragment>
              <IconButton
                size='small'
                aria-label='close'
                color='inherit'
                onClick={() => setNotificationOpen(false)}
                clean
              >
                <XMarkIconOutline />
              </IconButton>
            </React.Fragment>
          }
        />

        {allowDownload && rows && rows.length > 0 ? (
          <Tooltip title='Download CSV' aria-label='' disableInteractive>
            <IconButton
              onClick={() => {
                downloadCSV(rows);
              }}
              aria-label='download csv'
              className='n-absolute n-z-10 n-bottom-2 n-left-1'
              clean
            >
              <CloudArrowDownIconOutline />
            </IconButton>
          </Tooltip>
        ) : (
          <></>
        )}

        {wrapContent ? (
          <DataGrid
            {...commonGridProps}
            getRowHeight={() => 'auto'}
            sx={{
              ...customStyles,
              '&.MuiDataGrid-root .MuiDataGrid-cell': { wordBreak: 'break-word' },
            }}
          />
        ) : (
          <DataGrid {...commonGridProps} sx={customStyles} />
        )}
      </div>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: any, ownProps: ChartProps) => {
  const { id, pagenumber } = ownProps;
  let sessionPageSize: number | undefined;
  let sessionColumnVisibility: GridColumnVisibilityModel | undefined;
  let sessionColumnWidths: Record<string, number> | undefined;
  let sessionFilterModel: GridFilterModel | undefined;
  let sessionSortModel: GridSortModel | undefined;

  if (id && pagenumber !== undefined) {
    sessionPageSize = getSessionStorageValue(state, getTablePaginationKey(pagenumber, id));
    sessionColumnVisibility = getSessionStorageValue(state, getColumnVisibilityKey(pagenumber, id));
    sessionColumnWidths = getSessionStorageValue(state, getColumnWidthsKey(pagenumber, id));
    sessionFilterModel = getSessionStorageValue(state, getFilterModelKey(pagenumber, id));
    sessionSortModel = getSessionStorageValue(state, getSortModelKey(pagenumber, id));
  }
  return { sessionPageSize, sessionColumnVisibility, sessionColumnWidths, sessionFilterModel, sessionSortModel };
};

const mapDispatchToProps = (dispatch: any) => ({
  setSessionPageSize: (key: string, value: number) => {
    dispatch(setSessionStorageValue(key, value));
  },
  setSessionColumnVisibility: (key: string, value: GridColumnVisibilityModel) => {
    dispatch(setSessionStorageValue(key, value));
  },
  setSessionColumnWidths: (key: string, value: Record<string, number>) => {
    dispatch(setSessionStorageValue(key, value));
  },
  setSessionFilterModel: (key: string, value: GridFilterModel) => {
    dispatch(setSessionStorageValue(key, value));
  },
  setSessionSortModel: (key: string, value: GridSortModel) => {
    dispatch(setSessionStorageValue(key, value));
  },
});

const NeoTableChart = connect(mapStateToProps, mapDispatchToProps)(NeoTableChartComponent);
export { NeoTableChart };
export default NeoTableChart;
